'use server';
import { actionErrorMessage } from '@/shared/infrastructure/logging/actionError';

import type { ActionResult } from '@/shared/types/actions';
import { SessionService } from '@/contexts/authentication';
import { GAMES_JOBS_TENANT } from '../infrastructure/mazeGenerationQueue';
import {
  reserveMazeImage,
  refundMazeImage,
  InsufficientBalanceError,
  mazePricing,
} from '../infrastructure/mazeBilling';
import type { GameDifficulty } from '../types';

const ALLOWED_IMAGE_TYPES = new Set(['image/jpeg', 'image/png']);
const MAX_IMAGE_SIZE = 10 * 1024 * 1024; // 10 MB
const DEFAULT_CANVAS_SIZE = 800;

interface EnqueueMazeResult {
  jobId: string;
  mazeId: string;
}

/**
 * Enqueue a maze-generation job onto the shared owlcat_jobs `maze-generation`
 * queue. `ownerId` is the central identity id — echoed back on the job_results
 * row the consumer drains.
 *
 * BILLING: image mazes cost the admin-set image price (mazePricing) — reserved against the
 * central accounts wallet BEFORE enqueue (insufficient balance → hard error, no
 * job), refunded if upload/enqueue then fails; the reservation is settled when
 * generation completes (see readMazeResult / consumer). Template mazes stay FREE.
 */
export async function enqueueMazeGeneration(
  formData: FormData,
): Promise<ActionResult<EnqueueMazeResult>> {
  try {
    const user = await SessionService.getUser();
    const ownerId = user?.identityId ?? null;
    if (!ownerId) return { success: false, error: 'Not authenticated' };

    const difficulty = (formData.get('difficulty') as GameDifficulty) || 'medium';
    const templateId = formData.get('templateId') as string | null;
    const imageFile = formData.get('image') as File | null;

    if (!templateId && !imageFile) {
      return { success: false, error: 'Either image or templateId is required' };
    }

    const mazeId = crypto.randomUUID();
    const blobPath = `${ownerId}/${mazeId}/geometry.json`;

    const { getMazeGenerationQueue } = await import('../infrastructure/mazeGenerationQueue');

    if (imageFile) {
      // ── Image-based maze ──────────────────────────────────────
      if (!ALLOWED_IMAGE_TYPES.has(imageFile.type)) {
        return {
          success: false,
          error: 'Invalid image type. Only JPG and PNG are allowed.',
          code: 'INVALID_IMAGE_TYPE',
        };
      }
      if (imageFile.size > MAX_IMAGE_SIZE) {
        return {
          success: false,
          error: 'Image must be smaller than 10 MB.',
          code: 'IMAGE_TOO_LARGE',
        };
      }

      // Image mazes are PAID: need a linked accounts wallet (accountId). Anon /
      // not-yet-linked sessions cannot be charged → require sign-in.
      const accountId = user?.accountId ?? null;
      if (!accountId) {
        return {
          success: false,
          error: 'Sign in to generate image mazes.',
          code: 'SIGN_IN_REQUIRED',
        };
      }

      // Reserve the price BEFORE any work — insufficient balance stops here (no
      // upload, no job). Idempotent on the deterministic mazeId reservation.
      try {
        await reserveMazeImage(accountId, mazeId, (await mazePricing()).imagePriceTokens);
      } catch (e) {
        if (e instanceof InsufficientBalanceError) {
          return {
            success: false,
            error: 'Insufficient token balance. Please top up to generate an image maze.',
            code: 'INSUFFICIENT_BALANCE',
          };
        }
        throw e;
      }

      try {
        // Upload source image to storage (identity-scoped key so the worker + the
        // media proxy can resolve/authorize it).
        const { getImageStorage } = await import('@/contexts/media/storage/CharacterImageStorage');
        const storage = getImageStorage();
        const buffer = Buffer.from(await imageFile.arrayBuffer());
        const imageUrl = await storage.uploadMazeSource(ownerId, mazeId, buffer, imageFile.type);

        const jobId = await getMazeGenerationQueue().enqueue(
          {
            type: 'image-maze',
            familyId: ownerId,
            mazeId,
            imageUrl,
            difficulty,
            blobPath,
            canvasWidth: DEFAULT_CANVAS_SIZE,
            canvasHeight: DEFAULT_CANVAS_SIZE,
          },
          { tenant: GAMES_JOBS_TENANT },
        );

        return { success: true, data: { jobId, mazeId } };
      } catch (e) {
        // Upload/enqueue failed after the hold — release it (no silent token loss).
        const msg = e instanceof Error ? e.message : String(e);
        await refundMazeImage(mazeId, `Image maze enqueue failed: ${msg}`).catch(() => {});
        throw e;
      }
    }

    // ── Template-based maze ───────────────────────────────────
    const { getTemplateById } = await import(
      '../components/maze/assets/templates/maze-template-registry'
    );
    const template = getTemplateById(templateId!);
    if (!template) {
      return {
        success: false,
        error: `Template "${templateId}" not found.`,
        code: 'TEMPLATE_NOT_FOUND',
      };
    }

    // Read bundled SVG and extract contour on the server.
    const fs = await import('fs/promises');
    const path = await import('path');
    const svgPath = path.join(process.cwd(), 'public', template.svgPath);
    const svgContent = await fs.readFile(svgPath, 'utf-8');

    const { extractContourFromSvg } = await import(
      '../components/maze/helpers/contour-extraction'
    );
    const contour = extractContourFromSvg(svgContent);

    const jobId = await getMazeGenerationQueue().enqueue(
      {
        type: 'template-maze',
        familyId: ownerId,
        mazeId,
        templateId: templateId!,
        difficulty,
        blobPath,
        contour,
        canvasWidth: DEFAULT_CANVAS_SIZE,
        canvasHeight: DEFAULT_CANVAS_SIZE,
      },
      { tenant: GAMES_JOBS_TENANT },
    );

    return { success: true, data: { jobId, mazeId } };
  } catch (e: unknown) {
    return { success: false, error: actionErrorMessage('enqueueMazeGeneration', e) };
  }
}
